import { useState } from 'react'
import { FAIcon } from '@/components'
import { cn } from '@/lib/utils'
import { format } from '@/utils'
import { LogTabs } from '../absence-details/absence-logs/log-tabs'
import type { ActionFieldRecord } from '../absence-details/absence-logs/util'
import { parseActionFields } from '../absence-details/absence-logs/util'

type ActionFieldValue = ActionFieldRecord[string]

export type ActionLogListMode = 'single' | 'multiple'

export interface ActionLogListEntry {
  id: string
  actionFields: unknown
  createdAt: string
}

interface UseTabsParams<TLog extends ActionLogListEntry> {
  log: TLog
  fieldName: string
  value: ActionFieldValue
  oldValue: string
  newValue: string
}

interface Props<TLog extends ActionLogListEntry> {
  logs: readonly TLog[]
  noActivityText: string
  showDetailsText: string
  hideDetailsText: string
  oldTabLabel: string
  newTabLabel: string
  getLogSummary: (log: TLog, fields: ActionFieldRecord) => string
  getOrderedFieldEntries: (
    fields: ActionFieldRecord
  ) => Array<[string, ActionFieldValue]>
  getFieldLabel: (fieldName: string) => string
  formatFieldValue: (fieldName: string, raw: unknown) => string
  shouldUseOldNewTabs?: (params: UseTabsParams<TLog>) => boolean
  mode?: ActionLogListMode
  rowClassName?: string
  emptyClassName?: string
  fieldLabelClassName?: string
  fieldValueClassName?: string
}

export function ActionLogsList<TLog extends ActionLogListEntry>({
  logs,
  noActivityText,
  showDetailsText,
  hideDetailsText,
  oldTabLabel,
  newTabLabel,
  getLogSummary,
  getOrderedFieldEntries,
  getFieldLabel,
  formatFieldValue,
  shouldUseOldNewTabs,
  mode = 'single',
  rowClassName = 'px-5 py-3 sm:px-6',
  emptyClassName = 'px-5 py-5 text-sm text-stone-500 sm:px-6',
  fieldLabelClassName = 'text-sm font-medium text-stone-900 wrap-break-word',
  fieldValueClassName = 'text-sm text-stone-700 wrap-break-word',
}: Props<TLog>) {
  const [expandedIds, setExpandedIds] = useState<string[]>([])

  function isExpanded(id: string) {
    return expandedIds.includes(id)
  }

  function toggleExpanded(id: string) {
    setExpandedIds(prev => {
      const expanded = prev.includes(id)

      if (mode === 'single') {
        return expanded ? [] : [id]
      }

      if (expanded) {
        return prev.filter(entryId => entryId !== id)
      }

      return [...prev, id]
    })
  }

  if (!logs.length) {
    return <p className={emptyClassName}>{noActivityText}</p>
  }

  return (
    <ul className="divide-y divide-stone-200">
      {logs.map(log => {
        const fields = parseActionFields(log.actionFields)
        const expanded = isExpanded(log.id)
        const summary = getLogSummary(log, fields)

        return (
          <li key={log.id} className={rowClassName}>
            <button
              className="flex w-full items-center gap-3 text-left"
              type="button"
              aria-expanded={expanded}
              title={expanded ? hideDetailsText : showDetailsText}
              onClick={() => toggleExpanded(log.id)}
            >
              <p className="min-w-0 flex-1 truncate text-sm text-stone-700">
                <span className="text-stone-500">
                  {format(new Date(log.createdAt), 'dd.MM.yyyy HH:mm')}:
                </span>{' '}
                {summary}
              </p>

              <FAIcon
                className={cn(
                  'text-xs text-stone-500 transition-transform',
                  expanded && 'rotate-180'
                )}
                icon="chevron-down"
              />
            </button>

            {expanded && (
              <div className="mt-2.5 border-l-2 border-stone-200 pl-3.5">
                <div className="space-y-1.5">
                  {getOrderedFieldEntries(fields).map(([fieldName, value]) => {
                    const oldValue = formatFieldValue(fieldName, value.old_value)
                    const newValue = formatFieldValue(fieldName, value.new_value)
                    const hasOldValue = value.old_value !== undefined
                    const useTabs =
                      shouldUseOldNewTabs?.({
                        log,
                        fieldName,
                        value,
                        oldValue,
                        newValue,
                      }) ?? false

                    return (
                      <div key={`${log.id}-${fieldName}`}>
                        <p className={fieldLabelClassName}>
                          {getFieldLabel(fieldName)}
                        </p>

                        {useTabs ? (
                          <LogTabs
                            oldRaw={value.old_value}
                            newRaw={value.new_value}
                            oldText={oldValue}
                            newText={newValue}
                            oldLabel={oldTabLabel}
                            newLabel={newTabLabel}
                          />
                        ) : hasOldValue ? (
                          <p className={fieldValueClassName}>
                            <span>{oldValue}</span>
                            <span className="mx-1.5 text-stone-400">→</span>
                            <span>{newValue}</span>
                          </p>
                        ) : (
                          <p className={fieldValueClassName}>{newValue}</p>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
